import { useApolloClient } from "@apollo/client"
import { Intent } from "@blueprintjs/core"
import { cloneDeep, get, isString, omit, set, maxBy } from "lodash-es"
import React, { useMemo } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import CustomDateRange from "components/Atoms/CustomDateRange"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import useRouteForm from "hooks/useRouteForm"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import MutationOptions from "components/Organisms/MutationOptions"
import { CONTRACT_STATUSES } from "data/static.items"
import {
  ADD_UPDATE_VEHICLE_CONTRACT,
  CONTRACT_DETAILS,
} from "graphql/contract.graphql.gql"
import { CONTRACT_TYPES } from "graphql/selector.queries.gql"
import { VEHICLE_LIST_FRAG } from "graphql/vehicle/vehicle.fragments.gql"
import useErrors from "hooks/useErrors"
import contractModel from "models/contract.model"
import AppToaster from "utils/AppToaster"
import { graphqlError } from "utils/parseError"
import { removeTypename } from "utils/objectUtils"
import ContractExpenses from "./ContractExpenses"
const ContractRegistration = () => {
  const client = useApolloClient()
  const { id } = useParams()
  const location = useLocation() as any

  const vehicle = location?.state
  const vehicleId = location?.state?.id
  const isDisabled = location?.isDisabled
  const navigate = useNavigate()
  const [contract, setContract] = useRouteForm(CONTRACT_DETAILS, {
    default: { contractrecurringexpenseSet: [{}] },
  })

  function handleChange(path, value) {
    const contractCopy = cloneDeep(contract)
    set(contractCopy, path, value)
    setContract(contractCopy)
  }
  function addExpense() {
    setContract(prev => {
      const maxUnit = +maxBy(
        prev?.contractrecurringexpenseSet,
        c => (c as any).unitCount
      ) as any

      const maxUnitCount = maxUnit?.unitCount || 0
      return {
        ...prev,
        contractrecurringexpenseSet: prev?.contractrecurringexpenseSet?.concat({
          shift: null,
          cost: 0,
          unitCount: maxUnitCount + 1 ? maxUnitCount + 1 : 1,
        }) ?? [{ shift: null, cost: 0, unitCount: 1 }],
      }
    })
  }

  function removeExpense(index) {
    setContract(prev => ({
      ...prev,
      contractrecurringexpenseSet: prev?.contractrecurringexpenseSet?.filter(
        (_, idx) => idx !== index
      ),
    }))
  }
  function showToast() {
    AppToaster.show({
      intent: "success",
      icon: "tick",
      message: `Contract ${parseInt(id) ? "Edited" : "Added"} successfully `,
    })
  }
  const parsedVariables = useMemo(() => {
    return removeTypename({
      ...omit(contract, ["contractType", "contractrecurringexpenseSet"]),
      status: contract?.status?.id ?? contract?.status,
      entityIssued: vehicleId,
      contractTypeId: contract?.contractType?.id,
      recurringExpenses: contract?.contractrecurringexpenseSet?.map(c => ({
        ...omit(c, ["shift"]),
        shiftId: c?.shift?.id,
      })),
    })
  }, [contract, vehicleId])

  const [errors] = useErrors({ schema: contractModel, state: parsedVariables })

  return (
    <FormLayout id={id} type="Contract">
      <div className="flex items-baseline">
        <h5 className="mr-6 text-2xl">
          {contract?.contractCode && (
            <span>
              <span className="text-muted">Contract - </span>
              {contract?.contractCode && `${contract?.contractCode} for `}
            </span>
          )}
          <span className="text-muted">Vehicle - </span>{" "}
          {vehicle?.registrationNumber ?? "NA"}{" "}
          <span className="text-muted">
            ({`${vehicle?.vehicleType?.name ?? "NA"}`}{" "}
            {vehicle?.vehicleType?.category})
          </span>
        </h5>
      </div>
      <hr className="mt-0 mb-6" />
      <FormController
        disabled={isDisabled}
        source={contract}
        disableAll={true}
        onChange={handleChange}
      >
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <DynamicSelector
              big={true}
              required
              query={CONTRACT_TYPES}
              path="contractType"
              label="Contract Type"
            />
          </div>
          <div className="col-span-3">
            <StringInput
              helperText="initial odometer reading"
              path="resourceAmount"
              label="Resource Amount"
            />
          </div>
          <div className="col-span-3">
            <StringInput
              helperText="eg. driver allowance per day"
              path="dailyAllowance"
              label="Daily Allowance"
            />
          </div>
          <div className="col-span-3">
            <CustomDateRange
              popoverClassName="relative"
              label="Contract Validity"
              required
              value={[contract?.startDate, contract?.endDate]}
              onChange={(_, value) => {
                setContract(prev => ({
                  ...prev,
                  startDate: value?.[0],
                  endDate: value?.[1],
                }))
              }}
            />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-4 mt-3">
          <div className="col-span-3">
            <StaticSelector
              items={CONTRACT_STATUSES}
              path="status"
              label="Status"
              renderText={item => {
                if (item) {
                  if (item?.name) {
                    return item?.name
                  }
                  if (isString(item)) {
                    return item
                  }
                }
                return "Select Status"
              }}
              big={true}
              required
            />
          </div>
        </div>
        <ContractExpenses
          type={contract?.contractType}
          disabled={contract?.contractType ? false : true}
          expenses={contract?.contractrecurringexpenseSet}
          onRemove={removeExpense}
          onAdd={addExpense}
        />
        <div className="flex justify-end">
          <MutationOptions
            disabled={isDisabled}
            mutation={ADD_UPDATE_VEHICLE_CONTRACT}
            variables={parsedVariables}
            update={(_, { data }) => {
              const contract = data.addUpdateVehicleContract.contract

              const v = client.readFragment({
                fragment: VEHICLE_LIST_FRAG,
                id: `VehicleType:${vehicle?.id}`,
              })
              client.writeFragment({
                fragment: VEHICLE_LIST_FRAG,
                id: `VehicleType:${vehicle?.id}`,
                data: {
                  ...v,
                  currentContract: contract,
                },
              })
            }}
            validate={() => {
              if (errors.latest) {
                AppToaster.show({
                  message: errors.latest,
                  intent: "danger",
                })
                return false
              }
              return true
            }}
            onError={error => {
              AppToaster.show({
                message: graphqlError(error.message),
                intent: Intent.DANGER,
              })
            }}
            onSave={() => {
              showToast()
              navigate(-1)
            }}
            onSaveAndEdit={data => {
              if (!parseInt(id)) {
                navigate(
                  `/d/registration/contract/${get(data, "addUser.user.id")}`,
                  { replace: true }
                )
              }

              showToast()
            }}
          ></MutationOptions>
        </div>
      </FormController>
    </FormLayout>
  )
}

export default ContractRegistration
